import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtom } from 'jotai'
import Grid from '@mui/material/Grid'

import MarketplacesApi from '../../api/MarketplacesApi'
import UserEventApi from '../../api/UserEventApi'
import { getSectors } from '../issues/filters/utils/filters'
import DropdownTreeView, { DropdownTreeViewItems } from '../issues/filters/DropdownTreeView'
import DropdownSelect from '../components/DropdownSelect'
import { Marketplace } from '../../types/Marketplace'
import { warrantActiveTabAtom, warrantFiltersAtom } from '../../atoms/warrantAtom'
import ToggleWatchLists from '../components/ToggleWatchLists'
import { WarrantListItem } from '@finance-lab/utils/types'

enum WarrantFilterType {
    listed = 'listed',
    inTheMoney = 'inTheMoney',
    marketplaces = 'marketplaces',
    sectors = 'sectors',
}

type WarrantFilters = {
    [key in WarrantFilterType]: {
        value: string[]
    }
}

type WarrantsFilterComponent = React.FC<{
    warrants: WarrantListItem[]
    filters: WarrantFilters
    setFilters: (cb: (filters: WarrantFilters) => void) => void
}>

export const WarrantsFilter: WarrantsFilterComponent = ({ warrants }) => {
    const { t } = useTranslation()
    const sectors = useMemo(() => getSectors(warrants), [warrants])
    const [filters, setFilters] = useAtom(warrantFiltersAtom)
    const [tabValue] = useAtom(warrantActiveTabAtom)
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithWarrants, setMarketplacesWithWarrants] = useState<string[]>([])
    const api = new MarketplacesApi()
    const userEventApi = new UserEventApi()

    const listedAlternatives = useMemo(
        () => [
            { id: 'all', label: t('site.filters.showAllOptions') },
            { id: 'only-listed', label: t('site.filters.onlyListedOptions') },
            { id: 'only-unlisted', label: t('site.filters.onlyUnlistedOptions') },
        ],
        [t],
    )

    const inTheMoneyAlternatives = useMemo(
        () => [
            { id: 'all', label: t('site.filters.showAllOptions'), excludeInTabs: 'none' },
            {
                id: 'only-in-the-money',
                label: t('pages.warrants.filters.onlyInTheMoney'),
                excludeInTabs: 'previous',
            },
            {
                id: 'only-out-of-money',
                label: t('pages.warrants.filters.onlyOutOfTheMoney'),
                excludeInTabs: 'previous',
            },
            {
                id: 'only-floating-price',
                label: t('pages.warrants.filters.onlyFloatingPrice'),
                excludeInTabs: 'none',
            },
            {
                id: 'only-fixed-price',
                label: t('pages.warrants.filters.onlyFixedPrice'),
                excludeInTabs: 'none',
            },
        ],
        [t],
    )

    useEffect(() => {
        const fetchMarkets = async () => {
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!warrants.length) return

        const marketplaces = new Set<string>()

        warrants.forEach((warrant) => {
            marketplaces.add(warrant.marketplaceId)
        })

        setMarketplacesWithWarrants([...marketplaces])
    }, [warrants])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithWarrants.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {})

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithWarrants])

    const handleFilterChange = useCallback(
        async (filterType: string, selectedItems: boolean | string[]) => {
            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.FILTER_WARRANTS, {
                filterType,
                selectedItems,
            })
            setFilters((prevFilters) => ({
                ...prevFilters,
                [filterType]: { value: selectedItems },
            }))
        },
        [setFilters],
    )

    return (
        <Grid
            container
            spacing={3}
            sx={{
                pb: 6,
            }}
        >
            <Grid size={{ xs: 6, sm: 3 }}>
                <DropdownSelect
                    label={t('pages.warrants.filters.unlistedLabel')}
                    items={listedAlternatives}
                    selectedItems={filters.listed.value}
                    onChange={(selectedItems) => handleFilterChange('listed', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
                <DropdownSelect
                    label={t('pages.warrants.filters.inTheMoneyLabel')}
                    items={inTheMoneyAlternatives.filter((alternative) => alternative.excludeInTabs !== tabValue)}
                    selectedItems={filters.inTheMoney.value}
                    onChange={(selectedItems) => handleFilterChange('inTheMoney', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    label={t('site.filters.industry')}
                    pluralLabel={t('site.filters.industries')}
                    items={sectors}
                    selectedItems={filters.sectors.value}
                    onChange={(selectedItems) => handleFilterChange('sectors', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    label={t('site.filters.lists')}
                    pluralLabel={t('site.filters.lists')}
                    items={filteredMarketplaces}
                    selectedItems={filters.marketplaces.value}
                    onChange={(selectedItems) => handleFilterChange('marketplaces', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <ToggleWatchLists
                    onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                    value={filters.onlyInWatchLists.value}
                />
            </Grid>
        </Grid>
    )
}
